import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Trophy, Medal, Star, Heart, BookOpen, Clock, Calculator,
  Sunrise, Award, ChevronUp, ChevronDown, Minus
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { apiCall } from "../lib/api";

const btn3D = "shadow-[0_6px_0_rgba(0,0,0,0.12),0_8px_16px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_0_rgba(0,0,0,0.12),0_6px_12px_rgba(0,0,0,0.06)] hover:translate-y-[2px] active:shadow-[0_1px_0_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.04)] active:translate-y-[5px] transition-all duration-150 ease-out";

const BADGE_ICONS = {
  clock: Clock, calculator: Calculator, book: BookOpen, sunrise: Sunrise,
  heart: Heart, star: Star, trophy: Trophy,
};

const BADGE_COLORS = {
  blue: "bg-blue-100 text-blue-600 border-blue-200",
  purple: "bg-purple-100 text-purple-600 border-purple-200",
  green: "bg-green-100 text-green-600 border-green-200",
  orange: "bg-orange-100 text-orange-600 border-orange-200",
  red: "bg-red-100 text-red-600 border-red-200",
  yellow: "bg-yellow-100 text-yellow-600 border-yellow-200",
  amber: "bg-amber-100 text-amber-600 border-amber-200",
  gold: "bg-gradient-to-br from-yellow-200 to-amber-200 text-amber-700 border-amber-300",
};

const RANK_STYLES = [
  "bg-gradient-to-r from-yellow-400 to-amber-500 text-white",
  "bg-gradient-to-r from-gray-300 to-slate-400 text-white",
  "bg-gradient-to-r from-orange-400 to-amber-600 text-white",
];

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [rankings, setRankings] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [r, b] = await Promise.all([
          apiCall("GET", "/leaderboard/rankings"),
          apiCall("GET", "/leaderboard/badges"),
        ]);
        setRankings(r);
        setBadges(b);
      } catch (err) { console.error("Failed to fetch leaderboard:", err); }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-yellow-400 to-orange-400"><div className="spinner" /></div>;

  const top3 = rankings.slice(0, 3);
  const rest = rankings.slice(3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="leaderboard-page">
      <div className="bg-gradient-to-r from-[#002147] to-[#0a3d7a] text-white py-8">
        <div className="max-w-5xl mx-auto px-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl mb-4 border border-white/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <Trophy size={28} className="text-yellow-400" />
            <div>
              <h1 className="text-2xl font-bold">Leaderboard & Badges</h1>
              <p className="text-white/70 text-sm">Celebrating student excellence and achievements</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Top 3 Podium */}
        {top3.length >= 3 && (
          <div className="flex justify-center items-end gap-4 mb-2" data-testid="podium">
            {[1, 0, 2].map(idx => {
              const s = top3[idx];
              const isFirst = idx === 0;
              return (
                <div key={idx} className={`flex flex-col items-center ${isFirst ? "order-2" : idx === 1 ? "order-1" : "order-3"}`}>
                  <div className={`w-16 h-16 ${isFirst ? "w-20 h-20" : ""} rounded-full ${RANK_STYLES[idx]} flex items-center justify-center text-xl font-bold shadow-lg mb-2`}>
                    {s.avatar_initials}
                  </div>
                  <p className="font-bold text-[#002147] text-sm text-center">{s.student_name}</p>
                  <p className="text-xs text-gray-500">{s.grade}</p>
                  <Badge className={`mt-1 ${RANK_STYLES[idx]} border-0`}>{s.points} pts</Badge>
                  <div className={`mt-2 ${isFirst ? "h-24" : idx === 1 ? "h-16" : "h-12"} w-20 ${RANK_STYLES[idx]} rounded-t-lg flex items-start justify-center pt-2`}>
                    <span className="text-2xl font-bold opacity-80">#{idx + 1}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full Rankings */}
        <Card className="shadow-lg" data-testid="rankings-table">
          <CardHeader className="bg-[#002147] text-white rounded-t-lg py-3">
            <CardTitle className="text-lg flex items-center gap-2"><Medal size={18} /> Full Rankings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {rankings.map((s, i) => (
              <div key={i} className={`flex items-center gap-4 px-5 py-3.5 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"} ${i < rankings.length - 1 ? "border-b" : ""}`} data-testid={`rank-${i}`}>
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${i < 3 ? RANK_STYLES[i] : "bg-gray-100 text-gray-600"}`}>
                  {i + 1}
                </span>
                <div className="w-10 h-10 rounded-full bg-[#002147]/10 flex items-center justify-center text-sm font-bold text-[#002147] flex-shrink-0">
                  {s.avatar_initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#002147] text-sm truncate">{s.student_name}</p>
                  <p className="text-xs text-gray-400">{s.grade}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {(s.badges || []).slice(0, 3).map((bId, j) => {
                    const badge = badges.find(b => b.id === bId);
                    if (!badge) return null;
                    const Icon = BADGE_ICONS[badge.icon] || Star;
                    return (
                      <div key={j} className={`w-7 h-7 rounded-full flex items-center justify-center border ${BADGE_COLORS[badge.color] || "bg-gray-100"}`} title={badge.name}>
                        <Icon size={12} />
                      </div>
                    );
                  })}
                  {(s.badges || []).length > 3 && <span className="text-xs text-gray-400 self-center">+{s.badges.length - 3}</span>}
                </div>
                <span className="font-bold text-[#FF6B00] text-sm flex-shrink-0">{s.points} pts</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Badge Collection */}
        <h2 className="text-xl font-bold text-[#002147] flex items-center gap-2"><Award size={20} /> Badge Collection</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {badges.map((b, i) => {
            const Icon = BADGE_ICONS[b.icon] || Star;
            return (
              <Card key={i} className="hover:shadow-lg transition-shadow" data-testid={`badge-${b.id}`}>
                <CardContent className="p-4 text-center">
                  <div className={`w-14 h-14 rounded-2xl ${BADGE_COLORS[b.color] || "bg-gray-100"} flex items-center justify-center mx-auto mb-2 border`}>
                    <Icon size={24} />
                  </div>
                  <h3 className="font-semibold text-[#002147] text-sm">{b.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{b.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-4">
          <button onClick={() => navigate(-1)} className={`inline-flex items-center gap-2 px-5 py-3 bg-white text-[#002147] font-semibold rounded-xl border-2 border-[#002147]/20 ${btn3D} cursor-pointer select-none`} data-testid="back-btn-bottom">
            <ArrowLeft size={18} /> Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
